import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api/client';
import { territoryEndpoints } from '@/lib/api/endpoints';
import { territoryKeys } from '@/lib/queryKeys';
import type {
  CreateTerritoryPayload,
  Territory,
  TerritoryTech,
  TerritoryZip,
  UpdateTerritoryPayload,
} from '@/types/territory';

/** All territories for the tenant (ordered by sortOrder, then name). */
export function useTerritories() {
  return useApiQuery<Territory[]>({
    queryKey: territoryKeys.list(),
    queryFn: async ({ signal }) => {
      const res = await apiGet<{ data: Territory[] }>(operationsClient, territoryEndpoints.list(), {
        signal,
      });
      return res.data;
    },
  });
}

/** The zip service areas currently assigned to a territory. */
export function useTerritoryZips(territoryId: string | null) {
  return useApiQuery<TerritoryZip[]>({
    queryKey: territoryKeys.zips(territoryId ?? 'none'),
    enabled: Boolean(territoryId),
    queryFn: async ({ signal }) => {
      const res = await apiGet<{ data: TerritoryZip[] }>(
        operationsClient,
        territoryEndpoints.zips(territoryId!),
        { signal },
      );
      return res.data;
    },
  });
}

/** Technicians that serve a territory (derived from zip assignments). */
export function useTerritoryTechs(territoryId: string | null) {
  return useApiQuery<TerritoryTech[]>({
    queryKey: territoryKeys.techs(territoryId ?? 'none'),
    enabled: Boolean(territoryId),
    queryFn: async ({ signal }) => {
      const res = await apiGet<{ data: TerritoryTech[] }>(
        operationsClient,
        territoryEndpoints.techs(territoryId!),
        { signal },
      );
      return res.data;
    },
  });
}

export function useCreateTerritory() {
  const queryClient = useQueryClient();
  return useApiMutation<Territory, CreateTerritoryPayload>({
    mutationFn: (payload) =>
      apiPost<Territory, CreateTerritoryPayload>(
        operationsClient,
        territoryEndpoints.create(),
        payload,
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: territoryKeys.all() });
    },
  });
}

export function useUpdateTerritory() {
  const queryClient = useQueryClient();
  return useApiMutation<Territory, { id: string; payload: UpdateTerritoryPayload }>({
    mutationFn: ({ id, payload }) =>
      apiPut<Territory, UpdateTerritoryPayload>(
        operationsClient,
        territoryEndpoints.update(id),
        payload,
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: territoryKeys.all() });
    },
  });
}

export function useArchiveTerritory() {
  const queryClient = useQueryClient();
  return useApiMutation<Territory, string>({
    mutationFn: (id) => apiDelete<Territory>(operationsClient, territoryEndpoints.archive(id)),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: territoryKeys.all() });
    },
  });
}

/** Replace the exact set of zips that belong to a territory. */
export function useSetTerritoryZips() {
  const queryClient = useQueryClient();
  return useApiMutation<{ assigned: number; total: number }, { id: string; zipCodes: string[] }>({
    mutationFn: ({ id, zipCodes }) =>
      apiPut<{ assigned: number; total: number }, { zipCodes: string[] }>(
        operationsClient,
        territoryEndpoints.setZips(id),
        { zipCodes },
      ),
    onSettled: (_data, _err, vars) => {
      void queryClient.invalidateQueries({ queryKey: territoryKeys.all() });
      if (vars) {
        void queryClient.invalidateQueries({
          queryKey: territoryKeys.zips(vars.id),
        });
      }
    },
  });
}
